import React, { useState, useEffect } from 'react';
import { PageSkeleton } from '../DashboardLoader';
import {
  CreditCard,
  Search, 
  Filter, 
  Download, 
  Eye, 
  CheckCircle,
  XCircle,
  Clock,
  DollarSign,
  TrendingUp,
  AlertCircle,
  ArrowUpRight,
  ArrowDownLeft,
  Banknote,
  Smartphone,
  Calendar
} from 'lucide-react';
import { getAllPayments, getPaymentDetails, getPlatformCommissions, platformWithdraw } from '../../../services/adminApi';

const AdminPayments = () => {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterType, setFilterType] = useState('all');
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [commissions, setCommissions] = useState(null);
  const [showWithdrawModal, setShowWithdrawModal] = useState(false);
  const [withdrawAmount, setWithdrawAmount] = useState('');
  const [withdrawing, setWithdrawing] = useState(false);
  const [withdrawResult, setWithdrawResult] = useState(null);

  const statusOptions = [
    { id: 'all', name: 'Tous les statuts' },
    { id: 'completed', name: 'Terminés' },
    { id: 'pending', name: 'En attente' },
    { id: 'failed', name: 'Échoués' },
    { id: 'refunded', name: 'Remboursés' }
  ];

  const typeOptions = [
    { id: 'all', name: 'Tous les types' },
    { id: 'order_payment', name: 'Paiement commande' },
    { id: 'restaurant_payout', name: 'Paiement restaurant' },
    { id: 'platform_fee', name: 'Frais plateforme' },
    { id: 'refund', name: 'Remboursement' },
    { id: 'withdrawal', name: 'Retrait' }
  ];

  useEffect(() => {
    loadTransactions();
    loadCommissions();
  }, []);

  const loadCommissions = async () => {
    try {
      const data = await getPlatformCommissions();
      setCommissions(data);
    } catch (e) {
      console.error('Erreur commissions plateforme:', e);
    }
  };

  const handlePlatformWithdraw = async () => {
    const amount = parseFloat(withdrawAmount);
    if (!amount || amount <= 0) return;
    setWithdrawing(true);
    setWithdrawResult(null);
    try {
      const result = await platformWithdraw(amount);
      setWithdrawResult({ success: true, ...result });
      setWithdrawAmount('');
      loadCommissions();
    } catch (e) {
      setWithdrawResult({ success: false, message: e.message || 'Erreur lors du retrait' });
    } finally {
      setWithdrawing(false);
    }
  };

  const loadTransactions = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getAllPayments();
      const list = Array.isArray(data) ? data : (data?.payments ?? data?.transactions ?? []);
      setTransactions(list);
    } catch (error) {
      console.error('Erreur lors du chargement des transactions:', error);
      setError('Erreur lors du chargement des transactions');
      // On ne remplit plus avec des données mock : la liste restera vide en cas d'erreur,
      // ce qui reflète mieux l'état réel de l'API.
      setTransactions([]);
    } finally {
      setLoading(false);
    }
  };

  const safeTransactions = Array.isArray(transactions) ? transactions : [];
  const filteredTransactions = safeTransactions.filter(transaction => {
    const idStr = String(transaction?.id ?? '');
    const customerName = (transaction?.customer && typeof transaction.customer === 'object' && transaction.customer?.name != null)
      ? String(transaction.customer.name) : '';
    const restaurantName = (transaction?.restaurant && typeof transaction.restaurant === 'object' && transaction.restaurant?.name != null)
      ? String(transaction.restaurant.name) : '';
    const orderNumber = (transaction?.order && typeof transaction.order === 'object' && transaction.order?.number != null)
      ? String(transaction.order.number) : '';
    const matchesSearch = !searchTerm || idStr.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         customerName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         restaurantName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         orderNumber.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = filterStatus === 'all' || transaction?.status === filterStatus;
    const matchesType = filterType === 'all' || transaction?.type === filterType;
    return matchesSearch && matchesStatus && matchesType;
  });

  const stats = [
    {
      title: 'Total Transactions',
      value: safeTransactions.length.toString(),
      change: '+15%',
      icon: CreditCard,
    },
    {
      title: 'Volume Total',
      value: `${safeTransactions.reduce((sum, t) => sum + Math.abs(Number(t?.amount) || 0), 0).toFixed(0)}€`,
      change: '+22%',
      icon: DollarSign,
    },
    {
      title: 'Taux de Réussite',
      value: safeTransactions.length
        ? `${((safeTransactions.filter(t => t?.status === 'completed').length / safeTransactions.length) * 100).toFixed(1)}%`
        : '0%',
      change: '+2.1%',
      icon: CheckCircle,
    },
    {
      title: 'Frais Collectés',
      value: `${safeTransactions.reduce((sum, t) => sum + (Number(t?.fees?.total) || 0), 0).toFixed(2)}€`,
      change: '+18%',
      icon: TrendingUp,
    }
  ];

  const getStatusBadge = (status) => {
    const s = status ?? 'pending';
    const styles = {
      completed: 'bg-green-100 text-green-800',
      pending: 'bg-yellow-100 text-yellow-800',
      failed: 'bg-red-100 text-red-800',
      refunded: 'bg-blue-100 text-blue-800'
    };
    const labels = {
      completed: 'Terminé',
      pending: 'En attente',
      failed: 'Échoué',
      refunded: 'Remboursé'
    };
    const icons = {
      completed: <CheckCircle className="w-3 h-3 text-[#054625]" />,
      pending: <Clock className="w-3 h-3 text-yellow-600" />,
      failed: <XCircle className="w-3 h-3 text-red-600" />,
      refunded: <ArrowDownLeft className="w-3 h-3 text-blue-600" />
    };
    return (
      <span className={`inline-flex items-center px-2 py-1 text-xs font-medium rounded-full ${styles[s] || styles.pending}`}>
        {icons[s] || icons.pending}
        <span className="ml-1">{labels[s] || String(s)}</span>
      </span>
    );
  };

  const iconMuted = 'w-4 h-4 text-gray-500';

  const getTypeIcon = (type) => {
    switch (type) {
      case 'order_payment': return <CreditCard className={iconMuted} />;
      case 'restaurant_payout': return <ArrowUpRight className={iconMuted} />;
      case 'platform_fee': return <DollarSign className={iconMuted} />;
      case 'refund': return <ArrowDownLeft className={iconMuted} />;
      case 'withdrawal': return <Banknote className={iconMuted} />;
      default: return <CreditCard className={iconMuted} />;
    }
  };

  const getPaymentMethodIcon = (method) => {
    switch (method) {
      case 'card': return <CreditCard className={iconMuted} />;
      case 'mobile_money': return <Smartphone className={iconMuted} />;
      case 'bank_transfer': return <Banknote className={iconMuted} />;
      case 'cash': return <DollarSign className={iconMuted} />;
      default: return <CreditCard className={iconMuted} />;
    }
  };

  const getTypeName = (type) => {
    const typeMap = {
      'order_payment': 'Paiement commande',
      'restaurant_payout': 'Paiement restaurant',
      'platform_fee': 'Frais plateforme',
      'refund': 'Remboursement',
      'withdrawal': 'Retrait'
    };
    const key = typeof type === 'string' ? type : (type?.id ?? type?.name ?? '');
    return typeMap[key] || (typeof key === 'string' ? key : '');
  };

  const formatAmount = (amount) => {
    const num = Number(amount);
    if (Number.isNaN(num)) return '0.00€';
    const isNegative = num < 0;
    const absAmount = Math.abs(num);
    return `${isNegative ? '-' : ''}${absAmount.toFixed(2)}€`;
  };

  const formatDate = (dateString) => {
    if (dateString == null) return '—';
    const d = new Date(dateString);
    return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleViewDetails = async (transaction) => {
    try {
      const details = await getPaymentDetails(transaction.id);
      setSelectedTransaction(details);
      setShowDetailsModal(true);
    } catch (error) {
      console.error('Erreur lors du chargement des détails:', error);
      setError('Erreur lors du chargement des détails de la transaction');
      // Fallback avec les données de base
      setSelectedTransaction(transaction);
      setShowDetailsModal(true);
    }
  };

  const handleExport = () => {
    const rows = filteredTransactions;
    const header = ['id', 'type', 'amount', 'status', 'paymentMethod', 'createdAt', 'description'];
    const escape = (v) => {
      let s = v == null ? '' : String(v);
      // Injection de formule : Excel exécute une cellule commençant par = + - @ (les nombres restent intacts)
      if (/^[=+\-@\t\r]/.test(s) && Number.isNaN(Number(s))) s = `'${s}`;
      if (/[",\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
      return s;
    };
    const lines = [header.join(',')];
    for (const t of rows) {
      const created = t?.createdAt ?? t?.created_at ?? '';
      lines.push([
        escape(t?.id),
        escape(t?.type),
        escape(t?.amount),
        escape(t?.status),
        escape(t?.paymentMethod),
        escape(created),
        escape(t?.description),
      ].join(','));
    }
    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `transactions-namke-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (loading) return <PageSkeleton />;

  return (
    <div className="p-4 bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">Gestion des Paiements</h1>
        <p className="text-gray-600">Suivez toutes les transactions de la plateforme</p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {stats.map((stat, index) => (
            <div key={index} className="bg-white rounded-lg p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">{stat.title}</p>
                <p className="text-2xl font-bold text-gray-900">{stat.value}</p>
                <p className="text-sm text-gray-500">{stat.change}</p>
              </div>
              <div className="p-3 rounded-full bg-gray-100">
                <stat.icon className="h-6 w-6 text-gray-600" />
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Platform Commissions */}
      <div className="bg-[#FCFBF5] rounded-2xl p-6 mb-8 shadow-[0_2px_16px_rgba(5,70,37,0.07)] border border-[#054625]/10">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-5">
          <div>
            <h2 className="text-lg font-bold text-[#054625]">Commissions Plateforme</h2>
            <p className="text-sm text-gray-500 mt-0.5">Revenus Afrodine sur les commandes (15% sur la nourriture)</p>
          </div>
          <button
            onClick={() => { setShowWithdrawModal(true); setWithdrawResult(null); }}
            className="inline-flex items-center gap-2 px-4 py-2 bg-[#054625] text-white text-sm font-semibold rounded-xl hover:bg-[#054625]/90 transition-colors"
          >
            <Banknote className="w-4 h-4" />
            Retirer les fonds
          </button>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <div className="bg-white rounded-xl p-4 shadow-sm">
            <p className="text-xs text-gray-500 mb-1">Total collecté</p>
            <p className="text-xl font-bold text-[#054625]">{commissions ? `${Number(commissions.total_commissions).toFixed(2)} €` : '—'}</p>
          </div>
          <div className="bg-white rounded-xl p-4 shadow-sm">
            <p className="text-xs text-gray-500 mb-1">En attente (DB)</p>
            <p className="text-xl font-bold text-yellow-600">{commissions ? `${Number(commissions.pending_commissions).toFixed(2)} €` : '—'}</p>
          </div>
          <div className="bg-white rounded-xl p-4 shadow-sm">
            <p className="text-xs text-gray-500 mb-1">Disponible Stripe</p>
            <p className="text-xl font-bold text-[#054625]">
              {commissions?.stripe_balance?.available != null
                ? `${Number(commissions.stripe_balance.available).toFixed(2)} €`
                : <span className="text-sm text-gray-400">—</span>}
            </p>
          </div>
          <div className="bg-white rounded-xl p-4 shadow-sm">
            <p className="text-xs text-gray-500 mb-1">En transit Stripe</p>
            <p className="text-xl font-bold text-orange-500">
              {commissions?.stripe_balance?.pending != null
                ? `${Number(commissions.stripe_balance.pending).toFixed(2)} €`
                : <span className="text-sm text-gray-400">—</span>}
            </p>
          </div>
        </div>

        {commissions?.stripe_balance?.error && (
          <p className="mt-3 text-xs text-red-500">Erreur Stripe : {commissions.stripe_balance.error}</p>
        )}
      </div>

      {/* Withdraw Modal */}
      {showWithdrawModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6">
            <h3 className="text-lg font-bold text-[#054625] mb-1">Retrait commissions plateforme</h3>
            <p className="text-sm text-gray-500 mb-5">
              Solde disponible Stripe :{' '}
              <strong>{commissions?.stripe_balance?.available != null ? `${Number(commissions.stripe_balance.available).toFixed(2)} €` : '—'}</strong>
            </p>

            <label className="block text-sm font-medium text-gray-700 mb-1">Montant (€)</label>
            <input
              type="number"
              min="1"
              step="0.01"
              placeholder="Ex : 150.00"
              value={withdrawAmount}
              onChange={(e) => setWithdrawAmount(e.target.value)}
              className="w-full px-4 py-3 border border-gray-200 rounded-xl mb-4 focus:outline-none focus:ring-2 focus:ring-[#054625]/30"
            />

            {withdrawResult && (
              <div className={`rounded-xl p-3 mb-4 text-sm ${withdrawResult.success ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-700'}`}>
                {withdrawResult.success
                  ? `Retrait de ${Number(withdrawResult.amount).toFixed(2)} € initié (ID: ${withdrawResult.payout_id})`
                  : withdrawResult.message}
              </div>
            )}

            <div className="flex gap-3">
              <button
                onClick={() => setShowWithdrawModal(false)}
                className="flex-1 py-3 rounded-xl border border-gray-200 text-gray-700 font-medium hover:bg-gray-50 transition-colors"
              >
                Annuler
              </button>
              <button
                onClick={handlePlatformWithdraw}
                disabled={withdrawing || !withdrawAmount}
                className="flex-1 py-3 rounded-xl bg-[#054625] text-white font-semibold hover:bg-[#054625]/90 transition-colors disabled:opacity-50"
              >
                {withdrawing ? 'En cours...' : 'Confirmer le retrait'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-lg p-6 mb-6">
        <div className="flex flex-col lg:flex-row gap-4">
          {/* Search */}
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Rechercher une transaction..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[rgb(5, 70, 37)]"
              />
            </div>
          </div>

          {/* Status Filter */}
          <div className="flex items-center space-x-2">
            <Filter className="w-4 h-4 text-gray-600" />
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[rgb(5, 70, 37)]"
            >
              {statusOptions.map(option => (
                <option key={option.id} value={option.id}>
                  {option.name}
                </option>
              ))}
            </select>
          </div>

          {/* Type Filter */}
          <div className="flex items-center space-x-2">
            <CreditCard className="w-4 h-4 text-gray-600" />
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[rgb(5, 70, 37)]"
            >
              {typeOptions.map(option => (
                <option key={option.id} value={option.id}>
                  {option.name}
                </option>
              ))}
            </select>
          </div>

          {/* Export Button */}
          <button
            onClick={handleExport}
            className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 flex items-center gap-2"
          >
            <Download className="w-4 h-4 text-gray-600" />
            Exporter
          </button>
        </div>
        {error && (
          <div className="mt-4 p-4 bg-red-100 text-red-700 rounded-lg flex items-center gap-2">
            <AlertCircle className="w-5 h-5 text-red-600" />
            {error?.message || error?.toString() || 'Une erreur est survenue'}
          </div>
        )}
      </div>

      {/* Transactions Table */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">
                <th className="px-4 py-3">
                  Transaction
                </th>
                <th className="px-4 py-3">
                  Type
                </th>
                <th className="px-4 py-3">
                  Montant
                </th>
                <th className="px-4 py-3">
                  Client/Restaurant
                </th>
                <th className="px-4 py-3">
                  Méthode
                </th>
                <th className="px-4 py-3">
                  Statut
                </th>
                <th className="px-4 py-3">
                  Date
                </th>
                <th className="px-4 py-3">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filteredTransactions.map((transaction, index) => (
                <tr key={transaction?.id ?? index} className="hover:bg-gray-50">
                  <td className="px-4 py-3 whitespace-nowrap">
                    <div>
                      <div className="text-sm font-medium text-gray-900">{String(transaction?.id ?? '')}</div>
                      <div className="text-sm text-gray-500">{String(transaction?.description ?? '')}</div>
                    </div>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <div className="flex items-center space-x-2">
                      {getTypeIcon(transaction.type)}
                      <span className="text-sm text-gray-900">{getTypeName(transaction.type)}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <div>
                      <div className={`text-sm font-medium ${Number(transaction?.amount) < 0 ? 'text-red-600' : 'text-gray-900'}`}>
                        {formatAmount(transaction?.amount)}
                      </div>
                      {(transaction?.fees?.total != null && Number(transaction.fees.total) > 0) && (
                        <div className="text-xs text-gray-500">
                          Frais: {Number(transaction.fees.total).toFixed(2)}€
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <div>
                      {transaction.customer && typeof transaction.customer === 'object' ? (
                        <div>
                          <div className="text-sm font-medium text-gray-900">{String(transaction.customer?.name ?? '')}</div>
                          <div className="text-sm text-gray-500">{String(transaction.customer?.email ?? '')}</div>
                        </div>
                      ) : (
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {transaction.restaurant && typeof transaction.restaurant === 'object'
                              ? String(transaction.restaurant?.name ?? '')
                              : (transaction?.restaurant_id != null ? `Restaurant #${transaction.restaurant_id}` : '—')}
                          </div>
                          <div className="text-sm text-gray-500">Restaurant</div>
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <div className="flex items-center space-x-2">
                      {getPaymentMethodIcon(transaction.paymentMethod)}
                      <span className="text-sm text-gray-900 capitalize">
                        {String(transaction?.paymentMethod ?? '').replace('_', ' ')}
                      </span>
                    </div>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    {getStatusBadge(transaction.status)}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <div>
                      <div className="text-sm text-gray-900">{formatDate(transaction?.createdAt ?? transaction?.created_at)}</div>
                      {(transaction?.completedAt ?? transaction?.completed_at) && (
                        <div className="text-xs text-gray-500">
                          Terminé: {formatDate(transaction?.completedAt ?? transaction?.completed_at)}
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm font-medium">
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => handleViewDetails(transaction)}
                        className="p-1.5 border border-gray-200 rounded-md text-gray-500 hover:text-gray-700"
                        title="Voir détails"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {filteredTransactions.length === 0 && (
        <div className="text-center py-12">
          <CreditCard className="w-12 h-12 text-gray-500 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Aucune transaction trouvée</h3>
          <p className="text-gray-500">Aucune transaction ne correspond à vos critères de recherche</p>
        </div>
      )}

      {/* Transaction Details Modal */}
      {showDetailsModal && selectedTransaction && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-medium text-gray-900">Détails de la transaction</h3>
              <button
                onClick={() => setShowDetailsModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <XCircle className="w-6 h-6 text-gray-500" />
              </button>
            </div>
            
            <div className="space-y-6">
              {/* Transaction Info */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">ID Transaction</label>
                  <p className="text-sm text-gray-900">{String(selectedTransaction?.id ?? '')}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                  <p className="text-sm text-gray-900">{getTypeName(selectedTransaction?.type)}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Montant</label>
                  <p className={`text-sm font-medium ${Number(selectedTransaction?.amount) < 0 ? 'text-red-600' : 'text-gray-900'}`}>
                    {formatAmount(selectedTransaction?.amount)}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Statut</label>
                  {getStatusBadge(selectedTransaction.status)}
                </div>
              </div>

              {/* Payment Details */}
              <div>
                <h4 className="text-md font-medium text-gray-900 mb-3">Détails du paiement</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Méthode</label>
                    <p className="text-sm text-gray-900 capitalize">
                      {String(selectedTransaction?.paymentMethod ?? '').replace('_', ' ')}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Fournisseur</label>
                    <p className="text-sm text-gray-900">{String(selectedTransaction?.paymentProvider ?? selectedTransaction?.payment_intent_id ?? '—')}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">ID Transaction</label>
                    <p className="text-sm text-gray-900 font-mono">{String(selectedTransaction?.transactionId ?? selectedTransaction?.payment_intent_id ?? '')}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Montant net</label>
                    <p className="text-sm text-gray-900">{formatAmount(selectedTransaction?.netAmount ?? selectedTransaction?.amount)}</p>
                  </div>
                </div>
              </div>

              {/* Fees Breakdown */}
              {(selectedTransaction?.fees && typeof selectedTransaction.fees === 'object') && (
                <div>
                  <h4 className="text-md font-medium text-gray-900 mb-3">Détail des frais</h4>
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Frais plateforme</label>
                      <p className="text-sm text-gray-900">{(Number(selectedTransaction.fees?.platform) || 0).toFixed(2)}€</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Frais paiement</label>
                      <p className="text-sm text-gray-900">{(Number(selectedTransaction.fees?.payment) || 0).toFixed(2)}€</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Total frais</label>
                      <p className="text-sm font-medium text-gray-900">{(Number(selectedTransaction.fees?.total) || 0).toFixed(2)}€</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Customer/Restaurant Info */}
              <div>
                <h4 className="text-md font-medium text-gray-900 mb-3">
                  {selectedTransaction.customer ? 'Informations client' : 'Informations restaurant'}
                </h4>
                {selectedTransaction.customer ? (
                  <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Nom</label>
                  <p className="text-sm text-gray-900">{String(selectedTransaction.customer?.name ?? '')}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                  <p className="text-sm text-gray-900">{String(selectedTransaction.customer?.email ?? '')}</p>
                </div>
                  </div>
                ) : (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Restaurant</label>
                    <p className="text-sm text-gray-900">
                      {selectedTransaction.restaurant && typeof selectedTransaction.restaurant === 'object'
                        ? String(selectedTransaction.restaurant?.name ?? selectedTransaction.restaurant?.id ?? '')
                        : (selectedTransaction.restaurant_id != null ? `Restaurant #${selectedTransaction.restaurant_id}` : '—')}
                    </p>
                  </div>
                )}
              </div>

              {/* Order Info */}
              {selectedTransaction.order && (
                <div>
                  <h4 className="text-md font-medium text-gray-900 mb-3">Informations commande</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Numéro commande</label>
                      <p className="text-sm text-gray-900">{String(selectedTransaction.order?.number ?? selectedTransaction.order_id ?? '')}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">ID Commande</label>
                      <p className="text-sm text-gray-900">{String(selectedTransaction.order?.id ?? selectedTransaction.order_id ?? '')}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Timestamps */}
              <div>
                <h4 className="text-md font-medium text-gray-900 mb-3">Horodatage</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Créé le</label>
                    <p className="text-sm text-gray-900">{formatDate(selectedTransaction?.createdAt ?? selectedTransaction?.created_at)}</p>
                  </div>
                  {(selectedTransaction?.completedAt ?? selectedTransaction?.completed_at) && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Terminé le</label>
                      <p className="text-sm text-gray-900">{formatDate(selectedTransaction?.completedAt ?? selectedTransaction?.completed_at)}</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPayments;





